/* ============================================================================
 * useTreeOperations.ts - Tree expand/collapse and selection operations
 * ============================================================================ */

import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { ProjectNode } from '@/shared/types/projectData';

interface UseTreeOperationsParams {
  expandedNodeIds: Set<string>;
  selectedNodeIds: Set<string>;
  setExpandedNodeIds: Dispatch<SetStateAction<Set<string>>>;
  setSelectedNodeIds: Dispatch<SetStateAction<Set<string>>>;
  captureOperation: (
    type: string,
    category: 'view' | 'selection' | 'filter' | 'navigation' | 'preference' | 'interaction' | 'edit',
    before: unknown,
    after: unknown,
    description?: string,
    component?: string,
    skipHistory?: boolean
  ) => void;
}

/**
 * Hook for tree operations (expand/collapse/select)
 */
export function useTreeOperations({
  expandedNodeIds,
  selectedNodeIds,
  setExpandedNodeIds,
  setSelectedNodeIds,
  captureOperation
}: UseTreeOperationsParams) {
  
  // Toggle expanded state of a node
  const toggleExpanded = useCallback((nodeId: string) => {
    const nextExpanded = new Set(expandedNodeIds);
    const wasExpanded = nextExpanded.has(nodeId);

    if (wasExpanded) {
      nextExpanded.delete(nodeId);
    } else {
      nextExpanded.add(nodeId);
    }

    captureOperation(
      'toggle-expand',
      'selection',
      { expanded: Array.from(expandedNodeIds) },
      { expanded: Array.from(nextExpanded) },
      `${wasExpanded ? 'Collapse' : 'Expand'} node`,
      'TreeView'
    );

    setExpandedNodeIds(nextExpanded);
  }, [captureOperation, expandedNodeIds, setExpandedNodeIds]);

  // Expand a specific node
  const expandNode = useCallback((nodeId: string) => {
    if (expandedNodeIds.has(nodeId)) {
      return;
    }

    const nextExpanded = new Set(expandedNodeIds).add(nodeId);
    captureOperation(
      'toggle-expand',
      'selection',
      { expanded: Array.from(expandedNodeIds) },
      { expanded: Array.from(nextExpanded) },
      'Expand node',
      'TreeView'
    );
    setExpandedNodeIds(nextExpanded);
  }, [captureOperation, expandedNodeIds, setExpandedNodeIds]);

  // Collapse a specific node
  const collapseNode = useCallback((nodeId: string) => {
    if (!expandedNodeIds.has(nodeId)) {
      return;
    }

    const nextExpanded = new Set(expandedNodeIds);
    nextExpanded.delete(nodeId);
    captureOperation(
      'toggle-expand',
      'selection',
      { expanded: Array.from(expandedNodeIds) },
      { expanded: Array.from(nextExpanded) },
      'Collapse node',
      'TreeView'
    );
    setExpandedNodeIds(nextExpanded);
  }, [captureOperation, expandedNodeIds, setExpandedNodeIds]);

  // Expand all nodes that have children anywhere in the tree
  const expandAll = useCallback((nodes: ProjectNode[]) => {
    const expandableNodeIds: string[] = [];

    const visit = (treeNodes: ProjectNode[]) => {
      treeNodes.forEach((node) => {
        if ((node.children?.length ?? 0) > 0) {
          expandableNodeIds.push(node.id);
          visit(node.children!);
        }
      });
    };

    visit(nodes);

    const nextExpanded = new Set(expandableNodeIds);
    captureOperation(
      'toggle-expand',
      'selection',
      { expanded: Array.from(expandedNodeIds) },
      { expanded: Array.from(nextExpanded) },
      'Expand all nodes with children',
      'TreeView'
    );
    setExpandedNodeIds(nextExpanded);
  }, [captureOperation, expandedNodeIds, setExpandedNodeIds]);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    captureOperation(
      'toggle-expand',
      'selection',
      { expanded: Array.from(expandedNodeIds) },
      { expanded: [] },
      'Collapse all nodes with children',
      'TreeView'
    );
    setExpandedNodeIds(new Set());
  }, [captureOperation, expandedNodeIds, setExpandedNodeIds]);

  // Select a node
  const selectNode = useCallback((nodeId: string, multi?: boolean) => {
    let nextSelection: Set<string>;

    if (multi) {
      nextSelection = new Set(selectedNodeIds);
      if (nextSelection.has(nodeId)) {
        nextSelection.delete(nodeId);
      } else {
        nextSelection.add(nodeId);
      }
    } else {
      nextSelection = new Set([nodeId]);
    }

    captureOperation(
      'select-node',
      'selection',
      { selected: Array.from(selectedNodeIds) },
      { selected: Array.from(nextSelection) },
      multi ? 'Update multi-selection' : 'Select node',
      'TreeView'
    );

    setSelectedNodeIds(nextSelection);
  }, [captureOperation, selectedNodeIds, setSelectedNodeIds]);

  // Clear selection
  const clearSelection = useCallback(() => {
    captureOperation(
      'select-node',
      'selection',
      { selected: Array.from(selectedNodeIds) },
      { selected: [] },
      'Clear selection',
      'TreeView'
    );
    setSelectedNodeIds(new Set());
  }, [captureOperation, selectedNodeIds, setSelectedNodeIds]);

  // Select multiple nodes
  const selectNodes = useCallback((nodeIds: string[]) => {
    const nextSelection = new Set(nodeIds);
    captureOperation(
      'select-node',
      'selection',
      { selected: Array.from(selectedNodeIds) },
      { selected: Array.from(nextSelection) },
      `Select ${nodeIds.length} nodes`,
      'TreeView'
    );
    setSelectedNodeIds(nextSelection);
  }, [captureOperation, selectedNodeIds, setSelectedNodeIds]);

  return {
    toggleExpanded,
    expandNode,
    collapseNode,
    expandAll,
    collapseAll,
    selectNode,
    clearSelection,
    selectNodes
  };
}
